import { useEffect, useRef, useState } from 'react';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';

// Google ile giriş düğmesi (Google Identity Services). İstemci kimliği sunucudaki GOOGLE_CLIENT_ID
// secret'ından okunur (VITE_GOOGLE_CLIENT_ID tanımlıysa o öncelikli). Kimlik yoksa düğme görünmez.
// Google'dan gelen kimlik token'ı onCredential ile sunucuya gönderilir.
const ENV_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

let clientIdPromise: Promise<string> | null = null;
function loadClientId(): Promise<string> {
  if (ENV_CLIENT_ID) return Promise.resolve(ENV_CLIENT_ID);
  if (!clientIdPromise) {
    clientIdPromise = fetch(`https://${projectId}.supabase.co/functions/v1/make-server-df8bdc4f/auth/google-config`, {
      headers: { Authorization: `Bearer ${publicAnonKey}` },
    })
      .then(r => (r.ok ? r.json() : { clientId: '' }))
      .then(d => String(d?.clientId || ''))
      .catch(() => { clientIdPromise = null; return ''; });
  }
  return clientIdPromise;
}

declare global {
  interface Window { google?: any }
}

let scriptPromise: Promise<void> | null = null;
function loadGoogleScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => { scriptPromise = null; reject(new Error('Google betiği yüklenemedi')); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// Google kimlik token'ının içeriğini (ad, e-posta) okur — yalnızca form doldurmak için; güvenlik doğrulaması sunucuda yapılır
export function decodeGoogleCredential(credential: string): { email?: string; name?: string; email_verified?: boolean } {
  try {
    const part = credential.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(part.padEnd(part.length + ((4 - part.length % 4) % 4), '=')), ch => ch.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return {};
  }
}

export function GoogleSignInButton({ onCredential, language, text = 'signin_with' }: {
  onCredential: (credential: string) => void;
  language?: string;
  /** Google düğme metni: giriş / kayıt / devam */
  text?: 'signin_with' | 'signup_with' | 'continue_with';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onCredential);
  cb.current = onCredential;
  const [clientId, setClientId] = useState(ENV_CLIENT_ID);

  useEffect(() => {
    let cancelled = false;
    loadClientId().then(id => { if (!cancelled) setClientId(id); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    loadGoogleScript().then(() => {
      if (cancelled || !ref.current || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp: any) => { if (resp?.credential) cb.current(resp.credential); },
        ux_mode: 'popup',
      });
      ref.current.innerHTML = '';
      window.google.accounts.id.renderButton(ref.current, {
        type: 'standard', theme: 'outline', size: 'large', text, shape: 'rectangular',
        locale: language || 'tr', width: ref.current.offsetWidth || 320,
      });
    }).catch(() => { /* Google erişilemezse düğme görünmez; PIN ile giriş sürer */ });
    return () => { cancelled = true; };
  }, [language, clientId, text]);

  if (!clientId) return null;
  return <div ref={ref} className="w-full flex justify-center min-h-[44px]" />;
}
